import React, { useState, useEffect } from 'react';
import eventService from '../../services/eventService';
import ImageUpload from '../ImageUpload';

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    dates: [{ date: '', maxParticipants: '', description: '' }],
    pointsRequired: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await eventService.getEventsAdmin();
      if (response.data.success) {
        setEvents(response.data.events);
      }
    } catch (error) {
      setError('Fehler beim Laden der Events');
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (imageUrl) => {
    setFormData(prev => ({
      ...prev,
      image: imageUrl
    }));
  };

  const handleDateChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((date, i) => 
        i === index ? { ...date, [field]: value } : date
      )
    }));
  };

  const addDateSlot = () => {
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, { date: '', maxParticipants: '', description: '' }]
    }));
  };

  const removeDateSlot = (index) => {
    if (formData.dates.length > 1) {
      setFormData(prev => ({
        ...prev,
        dates: prev.dates.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!formData.name || !formData.description || !formData.pointsRequired) {
        throw new Error('Bitte füllen Sie alle Pflichtfelder aus');
      }

      if (formData.dates.some(date => !date.date || !date.maxParticipants)) {
        throw new Error('Bitte füllen Sie alle Terminfelder aus');
      }

      if (formData.pointsRequired < 0) {
        throw new Error('Punkte können nicht negativ sein');
      }

      if (formData.dates.some(date => date.maxParticipants < 1)) {
        throw new Error('Mindestens 1 Teilnehmer pro Termin erforderlich');
      }

      // Check if dates are in the future
      const now = new Date();
      const invalidDates = formData.dates.filter(date => new Date(date.date) <= now);
      if (invalidDates.length > 0) {
        throw new Error('Alle Termine müssen in der Zukunft liegen');
      }

      let response;
      if (editingEvent) {
        response = await eventService.updateEvent(editingEvent._id, formData);
      } else {
        response = await eventService.createEvent(formData);
      }
      
      if (response.data.success) {
        setSuccess(editingEvent ? 'Event erfolgreich aktualisiert!' : 'Event erfolgreich erstellt!');
        setFormData({
          name: '',
          description: '',
          image: '',
          dates: [{ date: '', maxParticipants: '' }],
          pointsRequired: ''
        });
        setShowCreateForm(false);
        setEditingEvent(null);
        loadEvents();
      }
    } catch (error) {
      setError(error.message || 'Fehler beim Speichern des Events');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name,
      description: event.description,
      image: event.image,
      dates: event.dates.map(date => ({
        _id: date._id,
        date: new Date(date.date).toISOString().slice(0, 16),
        maxParticipants: date.maxParticipants,
        description: date.description || '', // Beschreibung laden
        isActive: date.isActive
      })),
      pointsRequired: event.pointsRequired
    });
    setShowCreateForm(true);
  };

  const handleToggleActive = async (eventId) => {
    try {
      const event = events.find(e => e._id === eventId);
      const response = await eventService.updateEvent(eventId, { isActive: !event.isActive });
      if (response.data.success) {
        setSuccess(`Event ${event.isActive ? 'deaktiviert' : 'aktiviert'}!`);
        loadEvents();
      }
    } catch (error) {
      setError('Fehler beim Ändern des Event-Status');
    }
  };

  const handleToggleDateActive = async (eventId, dateId) => {
    try {
      const event = events.find(e => e._id === eventId);
      const updatedDates = event.dates.map(date => 
        date._id === dateId ? { ...date, isActive: !date.isActive } : date
      );
      const response = await eventService.updateEvent(eventId, { dates: updatedDates });
      if (response.data.success) {
        setSuccess('Termin-Status geändert!');
        loadEvents();
      }
    } catch (error) {
      setError('Fehler beim Ändern des Termin-Status');
    }
  };

  const handleDeleteDate = async (eventId, dateId) => {
    if (window.confirm('Sind Sie sicher, dass Sie diesen Termin löschen möchten?')) {
      try {
        const event = events.find(e => e._id === eventId);
        const updatedDates = event.dates.map(date => 
          date._id === dateId ? { ...date, invisible: true } : date
        );
        const response = await eventService.updateEvent(eventId, { dates: updatedDates });
        if (response.data.success) {
          setSuccess('Termin erfolgreich gelöscht!');
          loadEvents();
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Fehler beim Löschen des Termins';
        setError(errorMessage);
      }
    }
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Event löschen möchten?')) {
      try {
        await eventService.deleteEvent(eventId);
        setSuccess('Event erfolgreich gelöscht!');
        loadEvents();
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Fehler beim Löschen des Events';
        setError(errorMessage);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalParticipants = (event) => {
    return event.dates.reduce((total, date) => total + date.participants.length, 0);
  };

  const getTotalWaitlist = (event) => {
    return event.dates.reduce((total, date) => total + date.waitlist.length, 0);
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Lädt...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>📅 Event Management</h3>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            setEditingEvent(null);
            setFormData({
              name: '',
              description: '',
              image: '',
              dates: [{ date: '', maxParticipants: '' }],
              pointsRequired: ''
            });
          }}
        >
          {showCreateForm ? 'Abbrechen' : 'Neues Event erstellen'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Create Event Form */}
      {showCreateForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5>{editingEvent ? 'Event bearbeiten' : 'Neues Event erstellen'}</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label htmlFor="name" className="form-label">Event-Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label htmlFor="pointsRequired" className="form-label">Erforderliche Punkte *</label>
                    <input
                      type="number"
                      id="pointsRequired"
                      name="pointsRequired"
                      className="form-control"
                      value={formData.pointsRequired}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group mb-3">
                <label className="form-label">Beschreibung *</label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Event-Beschreibung eingeben..."
                  required
                  style={{
                    direction: 'ltr',
                    textAlign: 'left',
                    unicodeBidi: 'normal'
                  }}
                  dir="ltr"
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label">Event-Bild</label>
                <ImageUpload 
                  onImageUpload={handleImageUpload}
                  currentImage={formData.image}
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label">Termine *</label>
                {formData.dates.map((dateSlot, index) => (
                  <div key={index} className="card mb-2">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-5">
                          <label className="form-label">Datum und Uhrzeit</label>
                          <input
                            type="datetime-local"
                            className="form-control"
                            value={dateSlot.date}
                            onChange={(e) => handleDateChange(index, 'date', e.target.value)}
                            required
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Max. Teilnehmer</label>
                          <input
                            type="number"
                            className="form-control"
                            value={dateSlot.maxParticipants}
                            onChange={(e) => handleDateChange(index, 'maxParticipants', e.target.value)}
                            min="1"
                            required
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Beschreibung (optional)</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              placeholder="z.B. Thema der Stunde"
                              value={dateSlot.description || ''}
                              onChange={(e) => handleDateChange(index, 'description', e.target.value)}
                              maxLength="200"
                              style={{ 
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #dee2e6',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                fontFamily: 'Arial, sans-serif',
                                direction: 'ltr',
                                textAlign: 'left',
                                unicodeBidi: 'embed',
                                background: 'white',
                                color: 'black',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#e91e63';
                                e.target.style.direction = 'ltr';
                                e.target.style.textAlign = 'left';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#dee2e6';
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-1 d-flex align-items-end">
                          {formData.dates.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeDateSlot(index)}
                              title="Entfernen"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addDateSlot}
                >
                  + Weiteren Termin hinzufügen
                </button>
              </div>

              <div className="form-group">
                <button type="submit" className="btn btn-primary me-2" disabled={formLoading}>
                  {formLoading ? 'Speichere...' : (editingEvent ? 'Event aktualisieren' : 'Event erstellen')}
                </button>
                {editingEvent && (
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingEvent(null);
                      setShowCreateForm(false);
                      setFormData({
                        name: '',
                        description: '',
                        image: '',
                        dates: [{ date: '', maxParticipants: '' }],
                        pointsRequired: ''
                      });
                    }}
                  >
                    Abbrechen
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Events List */}
      <div className="row">
        {events.length === 0 ? (
          <div className="col-12">
            <div className="text-center">
              <p>Keine Events vorhanden.</p>
            </div>
          </div>
        ) : (
          events.map(event => (
            <div key={event._id} className="col-md-6 mb-4">
              <div className={`card ${!event.isActive ? 'opacity-75' : ''}`}>
                {event.image && (
                  <img src={event.image} className="card-img-top" alt={event.name} style={{height: '200px', objectFit: 'cover'}} />
                )}
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h5 className="card-title">{event.name}</h5>
                    <div>
                      <span className={`badge ${event.isActive ? 'bg-success' : 'bg-secondary'} me-2`}>
                        {event.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                  </div>
                  <div className="mb-2" dangerouslySetInnerHTML={{ __html: event.description.substring(0, 100) + '...' }} />
                  
                  <div className="row text-center mb-3">
                    <div className="col-4">
                      <strong>{event.pointsRequired}</strong><br />
                      <small className="text-muted">Punkte</small>
                    </div>
                    <div className="col-4">
                      <strong>{getTotalParticipants(event)}</strong><br />
                      <small className="text-muted">Teilnehmer</small>
                    </div>
                    <div className="col-4">
                      <strong>{getTotalWaitlist(event)}</strong><br />
                      <small className="text-muted">Warteliste</small>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h6>Termine ({event.dates.length}):</h6>
                    {event.dates
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((dateSlot, index) => (
                        <div key={dateSlot._id} className="border rounded p-2 mb-2">
                          <div className="d-flex justify-content-between">
                            <div>
                              <strong>{formatDate(dateSlot.date)}</strong><br />
                              <small>
                                {dateSlot.participants.length}/{dateSlot.maxParticipants} Teilnehmer
                                {dateSlot.waitlist.length > 0 && ` • ${dateSlot.waitlist.length} Warteliste`}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className={`badge ${dateSlot.isFull ? 'bg-warning' : 'bg-success'}`}>
                                {dateSlot.isFull ? 'Voll' : 'Verfügbar'}
                              </span>
                              <span className={`badge ${dateSlot.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                {dateSlot.isActive ? 'Aktiv' : 'Inaktiv'}
                              </span>
                              <button
                                className={`btn btn-sm ${dateSlot.isActive ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                                onClick={() => handleToggleDateActive(event._id, dateSlot._id)}
                                title={dateSlot.isActive ? 'Termin deaktivieren' : 'Termin aktivieren'}
                              >
                                {dateSlot.isActive ? '👁️' : '🚫'}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteDate(event._id, dateSlot._id)}
                                title="Termin löschen"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => handleEdit(event)}
                    >
                      Bearbeiten
                    </button>
                    <button
                      className={`btn btn-sm ${event.isActive ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleActive(event._id)}
                    >
                      {event.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    </button>
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(event._id)}
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EventManagement;
